import { useEffect, useState } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import useAuth from "@/hooks/useAuth";
import hasPermission from "@/hooks/hasPermission";
import TableSkeleton from "@/components/skeleton/table-skeletion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, PenLine, Star } from "lucide-react";
import { PrinterDialog } from "@/components/dialog/printer-dialog";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import {
  getAllPrinterApi,
  addPrinterApi,
  updatePrinterApi,
  deletePrinterApi,
} from "@/api/settings/printer-settings";
import { toast } from "react-hot-toast";


function PrinterList() {  
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch all printers
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllPrinterApi(user?.restaurant_id);
      if (response.status === "success") {
        setData(response.data?.printers || []);
      } else {
        toast.error(response.message || "Failed to fetch printers");
      }
    } catch (error) {
      toast.error("Error fetching printers");
    } finally {
      setLoading(false);
    }
  };

  // Add printer handler
  const handleAddPrinter = async (printerData) => {
    setSubmitting(true);
    try {
      const payload = {
        ...printerData,
        restaurant_id: user?.restaurant_id,
      };
      const response = await addPrinterApi(payload);
      if (response.status === "success") {
        toast.success("Printer added successfully");
        setIsAddDialogOpen(false);
        fetchData();
      } else {
        toast.error(response.message || "Failed to add printer");
      }
    } catch (error) {
      toast.error("Error adding printer");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit printer handler
  const handleEditPrinter = async (printerData) => {
    setSubmitting(true);
    try {
      const response = await updatePrinterApi(selectedPrinter.id, printerData);
      if (response.status === "success") {
        toast.success("Printer updated successfully");
        setIsEditDialogOpen(false);
        setSelectedPrinter(null);
        fetchData();
      } else {
        toast.error(response.message || "Failed to update printer");
      }
    } catch (error) {
      toast.error("Error updating printer");
    } finally {
      setSubmitting(false);
    }
  };

  // Set default till printer handler
  const handleSetDefaultPrinter = async (printer) => {
    setSubmitting(true);
    try {
      const menuItemIds = Array.isArray(printer.menu_items)
        ? printer.menu_items.map((mi) => (typeof mi === "object" ? mi.id : mi))
        : [];

      const payload = {
        name: printer.name,
        ip: printer.ip,
        port: printer.port,
        printer_type: printer.printer_type,
        printer_slip_id:
          printer.printer_slip_id ??
          (typeof printer.printer_slip === "object" ? printer.printer_slip?.id : printer.printer_slip) ??
          "",
        menu_items: menuItemIds,
        status: printer.status || "active",
        is_default: true,
        is_default_till: true,
        default_till: true,
        restaurant_id: user?.restaurant_id,
      };

      const response = await updatePrinterApi(printer.id, payload);
      if (response.status === "success" || response.status === true) {
        toast.success("Printer set as default till printer successfully");
        fetchData();
      } else {
        toast.error(response.message || "Failed to set default printer");
      }
    } catch (error) {
      toast.error("Error setting default printer");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete printer handler
  const handleDeletePrinter = async () => {
    setSubmitting(true);
    try {
      const response = await deletePrinterApi(selectedPrinter.id);
      if (response.status === "success") {
        toast.success("Printer deleted successfully");
        setIsDeleteDialogOpen(false);
        setSelectedPrinter(null);
        fetchData();
      } else {
        toast.error(response.message || "Failed to delete printer");
      }
    } catch (error) {
      toast.error("Error deleting printer");
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit dialog
  const handleEditClick = (printer) => {
    setSelectedPrinter(printer);
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const handleDeleteClick = (printer) => {
    setSelectedPrinter(printer);
    setIsDeleteDialogOpen(true);
  };

  useEffect(() => {
    if (user?.restaurant_id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.restaurant_id]);

  if (loading) return <TableSkeleton links={backofficeLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Printer List" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "/backoffice/settings" },
        { name: "Printer Management", path: "/backoffice/printer-management" },
      ]}
    >
      <div className="container flex flex-col gap-4 mt-5">
        {hasPermission("back_office.settings.add_printer") && (
          <button
            className="w-max self-end bg-color6 px-4 py-2 rounded-md"
            onClick={() => setIsAddDialogOpen(true)}
          >
            Add Printer
          </button>
        )}
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">No</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Port</TableHead>
              <TableHead>Printer Type</TableHead>
              <TableHead>Menu Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => {
                const isDefault = Boolean(
                  item?.is_default ||
                  item?.is_default_till ||
                  item?.default_till ||
                  item?.is_default_printer
                );
                const isTillPrinter = item?.printer_type?.toLowerCase() === "till";

                return (
                  <TableRow key={item?.id || index}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell>{item?.name}</TableCell>
                    <TableCell className="capitalize">{item?.ip || "N/A"}</TableCell>
                    <TableCell>{item?.port || "N/A"}</TableCell>
                    <TableCell className="capitalize">{item?.printer_type || "N/A"}</TableCell>
                    <TableCell>
                      {item?.menu_items && Array.isArray(item.menu_items)
                        ? item.menu_items
                            .map((mi) => mi.name) // take name from each object
                            .join(", ")
                        : item?.menu_items || "N/A"}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          item?.status === "active"
                            ? "bg-green-600/20 text-green-500"
                            : "bg-red-600/20 text-red-500"
                        }`}
                      >
                        {item?.status?.charAt(0).toUpperCase() +
                          item?.status?.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 h-8 justify-end">
                        {isTillPrinter && (
                          isDefault ? (
                            <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 font-medium inline-flex items-center gap-1">
                              <Star size={12} className="fill-yellow-400" /> Default
                            </span>
                          ) : (
                            hasPermission("back_office.settings.update_printer") && (
                              <button
                                onClick={() => handleSetDefaultPrinter(item)}
                                className="px-2 py-1 text-xs rounded bg-cyellow text-black hover:bg-yellow-400 font-medium transition-colors whitespace-nowrap"
                                disabled={submitting}
                              >
                                Make Default
                              </button>
                            )
                          )
                        )}

                        {hasPermission("back_office.settings.update_printer") && (
                          <span className="cursor-pointer" title="Edit Printer">
                            <PenLine
                              onClick={() => handleEditClick(item)}
                              className="text-cyellow"
                              size={18}
                            />
                          </span>
                        )}

                        {hasPermission("back_office.settings.delete_printer") && (
                          <span title="Delete Printer">
                            <Trash2
                              onClick={() => handleDeleteClick(item)}
                              className="text-cred cursor-pointer"
                              size={18}
                            />
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Printer Dialog */}
      <PrinterDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmit={handleAddPrinter}
        isLoading={submitting}
        restaurantId={user?.restaurant_id}
      />

      {/* Edit Printer Dialog */}
      <PrinterDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedPrinter(null);
        }}
        onSubmit={handleEditPrinter}
        editData={selectedPrinter}
        isLoading={submitting}
        restaurantId={user?.restaurant_id}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedPrinter(null);
        }}
        onConfirm={handleDeletePrinter}
        title="Are You Sure ?"
        description={`Are you want to Delete this item ?`}
        yesBtnTxt="Yes"
        noBtnTxt="Cancel"
        isLoading={submitting}
      />
    </Layout2>
  );
}

export default PrinterList;
